import React, { useContext, useEffect, useReducer, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import { useMediaQuery } from "@material-ui/core";
import {
  Avatar,
  Button,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField
} from "@material-ui/core";
import ReplyOutlinedIcon from '@material-ui/icons/ReplyOutlined';
import SearchIcon from "@material-ui/icons/Search";

import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import ConfirmationModal from "../../components/ConfirmationModal/";
import DispatchBlackListContactsModal from "../../components/DispatchBlackListContactsModal";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import SocketDispatchBlackListContacts from "../../context/Socket/Listeners/SocketDispatchBlackListContacts";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import Title from "../../components/Title";
import toastError from "../../errors/toastError";
import WhatsappOfficialImg from "../../assets/miscJpg/whatsappOfficial.jpg";

import DefaultFunctionsUtils from "../../utils/classes/DefaultFunctionsUtils";
import DefaultValuesUtils from "../../utils/classes/DefaultValuesUtils";
import JidUtils from "../../utils/classes/JidUtils";

import { useGlobalRegisters } from "../../context/GlobalRegisters/GlobalRegistersContext";

const reducer = (state, action) => {
  if (action.type === "LOAD_DISPATCH_BLACKLIST_CONTACTS") {
    const blackListContacts = action.payload;
    const newBlackListContacts = [];

    blackListContacts.forEach((blackListContact) => {
      const blackListContactIndex = state.findIndex((blc) => blc.id === blackListContact.id);
      if (blackListContactIndex !== -1) state[blackListContactIndex] = blackListContact;
      else newBlackListContacts.push(blackListContact);
    });

    return [...state, ...newBlackListContacts]
  }

  if (action.type === "UPDATE_DISPATCH_BLACKLIST_CONTACTS") {
    const blackListContact = action.payload;
    const searchParam = action.searchParam;
    const blackListContactIndex = state.findIndex((blc) => blc.id === blackListContact.id);

    if (!blackListContact.contact.name.toLowerCase().includes(searchParam)) {
      return [...state];
    }
    else if (blackListContactIndex !== -1) {
      state[blackListContactIndex] = blackListContact;
      return [...state];
    }
    else {
      return [blackListContact, ...state];
    }
  }

  if (action.type === "DELETE_DISPATCH_BLACKLIST_CONTACTS") {
    const blackListContactId = action.payload;
    const blackListContactIndex = state.findIndex((blc) => blc.id === blackListContactId);
    if (blackListContactIndex !== -1) state.splice(blackListContactIndex, 1);
    return [...state];
  }

  if (action.type === "RESET") { return []; }
};

const useStyles = makeStyles((theme) => ({
  floatingButton: {
    transition: "transform 0.3s",
		"&:hover": { transform: "translateY(-5px)", },
  },

  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },

  dispatchBlackListContactsModal: {
    overflowY: "scroll",
    height: "90%",
    ...theme.scrollbarStyles,
    [theme.breakpoints.down("sm")]: { maxHeight: "80%", },
  },

  icon: { color: theme.palette.text.primary, },

  actionButton: { "&:hover": { color: theme.palette.primary.main, }, },
}));

const DispatchContactsManager = () => {
  //  ***************
  //  ** Variables **
  //  ***************
  const classes = useStyles();
  const history = useHistory();
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const { user } = useContext(AuthContext);

  const [dispatchBlackListContacts, dispatch] = useReducer(reducer, []);

  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [hasMore, setHasMore] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);

  const [selectedBlackListContact, setSelectedBlackListContact] = useState(null);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [blackListContactModalOpen, setBlackListContactModalOpen] = useState(false);

  const { setShouldSyncRouteOnMenu } = useGlobalRegisters();



  //  *****************
  //  ** Use Effects **
  //  *****************
  useEffect(() => {
    if (user.profile === "user" || !user.configEnabled) {
      DefaultFunctionsUtils.redirectToTicketsPage(history);
      setShouldSyncRouteOnMenu(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);

    const delayDebounceFn = setTimeout(async () => {
      const fetchBlackListContacts = async () => {
        try {
          setLoading(false);
          const { data } = await api.get("/dispatchBlackListContacts", { params: { pageNumber, searchParam } });

          dispatch({ type: "LOAD_DISPATCH_BLACKLIST_CONTACTS", payload: data.dispatchBlackListContacts });
          setHasMore(data.hasMore);
          setLoading(false);
        }
        catch (exception) {
          console.log("Dispatch BlackList Contacts Manager Use-Effect Exception:", exception);
          toastError(exception);
        }
      };

      await fetchBlackListContacts();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber]);



  //  ***************
  //  ** Functions **
  //  ***************
  const handleResetStates = () => {
    setSearchParam("");
    setPageNumber(1);
    setHasMore(false);
  };

  const handleOpenDispatchBlackListContactsModal = () => {
    setBlackListContactModalOpen(true);
  };

  const handleCloseDispatchBlackListContactsModal = () => {
    setBlackListContactModalOpen(false);
  };

  const handleChange = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleScroll = (event) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) setPageNumber((prevState) => prevState + 1);
  };

  const handleOpenConfirmationModal = (blackListContact) => {
    setSelectedBlackListContact(blackListContact);
    setConfirmationModalOpen(true);
  };

  const handleCloseConfirmationModal = () => {
    setConfirmationModalOpen(false);
    setTimeout(() => setSelectedBlackListContact(null), 500);
  };

  const handleRemoveBlackListContact = async () => {
    try {
      setLoading(true);
      await api.delete(`/dispatchBlackListContacts/${selectedBlackListContact.id}`);
    }
    catch (exception) {
      console.log("Dispatch Contacts Manager - handleRemoveBlackListContact Exception:", exception);
      toast.error(exception);
    }
    finally {
      handleCloseConfirmationModal();
      handleResetStates();
      setTimeout(() => setLoading(false), 500);
    }
  };


  
  //  ************
  //  ** Return **
  //  ************
  return (
    <MainContainer>
      <SocketDispatchBlackListContacts dispatch={dispatch} searchParam={searchParam} />

      <DispatchBlackListContactsModal open={blackListContactModalOpen} onClose={handleCloseDispatchBlackListContactsModal} />

      <ConfirmationModal
        title={`${i18n.t("dispatchBlackListContacts.confirmationModal.title")} ${selectedBlackListContact?.contact?.name} (${selectedBlackListContact?.whatsapp?.name})?`}
        open={confirmationModalOpen}
        onClose={handleCloseConfirmationModal}
        onConfirm={async () => await handleRemoveBlackListContact()}
      />

      <MainHeader>
        <Title>{i18n.t("dispatchBlackListContacts.title")}</Title>

        <MainHeaderButtonsWrapper>
          <TextField
            placeholder={i18n.t("dispatchBlackListContacts.searchPlaceholder")}
            type="search"
            value={searchParam}
            onChange={handleChange}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon style={{ color: "gray" }} /></InputAdornment> }}
          />

          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenDispatchBlackListContactsModal}
            className={classes.floatingButton}
          >
            {i18n.t("dispatchBlackListContacts.buttons.add")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        <div className={classes.dispatchBlackListContactsModal} onScroll={handleScroll}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {isSmallScreen && <TableCell align="center">{i18n.t("dispatchBlackListContacts.table.actions")}</TableCell>}
                <TableCell padding="checkbox" />
                <TableCell>{i18n.t("dispatchBlackListContacts.table.name")}</TableCell>
                <TableCell align="center">{i18n.t("dispatchBlackListContacts.table.whatsApp")}</TableCell>
                <TableCell>{i18n.t("dispatchBlackListContacts.table.chip")}</TableCell>
                {!isSmallScreen && <TableCell align="center">{i18n.t("dispatchBlackListContacts.table.actions")}</TableCell>}
              </TableRow>
            </TableHead>

            <TableBody>
              <>
                {dispatchBlackListContacts.map((dispatchBlackListContact) => (
                  <TableRow key={dispatchBlackListContact.id}>
                    {isSmallScreen && (
                      <TableCell align="center">
                        <IconButton
                          className={classes.icon}
                          size="small"
                          onClick={() => handleOpenConfirmationModal(dispatchBlackListContact)}
                        >
                          <ReplyOutlinedIcon className={classes.actionButton} />
                        </IconButton>
                      </TableCell>
                    )}

                    <TableCell style={{ paddingRight: 0 }}><Avatar src={dispatchBlackListContact.contact.name === DefaultValuesUtils.SPECIAL_NAMES.whatsapp ? WhatsappOfficialImg : dispatchBlackListContact.contact.profilePicUrl} /></TableCell>
                    <TableCell>{dispatchBlackListContact.contact.name}</TableCell>
                    <TableCell align="center">{JidUtils.handleShowJid(dispatchBlackListContact.contact)}</TableCell>
                    <TableCell>{dispatchBlackListContact.whatsapp.name}</TableCell>

                    {!isSmallScreen && (
                      <TableCell align="center">
                        <IconButton
                          className={classes.icon}
                          size="small"
                          onClick={() => handleOpenConfirmationModal(dispatchBlackListContact)}
                        >
                          <ReplyOutlinedIcon className={classes.actionButton} />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))}

                {loading && <TableRowSkeleton avatar columns={3} />}
              </>
            </TableBody>
          </Table>
        </div>
      </Paper>
    </MainContainer>
  );
};

export default DispatchContactsManager;
